/**
 * 📊 Reportes Guardados — lista y ejecución de reportes
 * Ruta: /apps/reports/saved
 */

import { useState, useEffect, useCallback , useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Modal,
  TextField,
  Select,
  Badge,
  EmptyState,
  Divider,
  Box,
  Spinner,
  InlineGrid,
  Checkbox,
  IndexTable,
  Pagination,
} from '@/lib/polaris';
import {
  ChartLineIcon,
  PlusIcon,
  EditIcon,
  DeleteIcon,
  ExportIcon,
  PlayIcon,
} from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';

interface SavedReport {
  id: string;
  name: string;
  description: string | null;
  data_source: string;
  columns: Array<{ field: string; label: string }>;
  filters: Array<{ field: string; op: string; value: unknown }>;
  sort_by: string | null;
  sort_dir: string;
  group_by: string | null;
  aggregation: string | null;
  agg_field: string | null;
  date_range: string;
  is_shared: boolean;
  run_count: number;
  last_run_at: string | null;
  created_at: string;
}

interface ReportResult {
  mode: string;
  data: Record<string, unknown>[];
  total: number;
  page: number;
  page_size: number;
}

const DATA_SOURCE_OPTIONS = [
  { label: 'Órdenes', value: 'orders' },
  { label: 'Items de orden', value: 'order_items' },
  { label: 'Deals (CRM)', value: 'crm_deals' },
  { label: 'Leads (CRM)', value: 'crm_leads' },
  { label: 'Llamadas (CRM)', value: 'crm_calls' },
  { label: 'Emails', value: 'email_messages' },
  { label: 'Productos', value: 'products' },
  { label: 'Clientes', value: 'customers' },
  { label: 'Pagos', value: 'payments' },
];

const DATE_RANGE_OPTIONS = [
  { label: 'Últimos 7 días', value: '7d' },
  { label: 'Últimos 30 días', value: '30d' },
  { label: 'Últimos 90 días', value: '90d' },
  { label: 'Este año', value: 'ytd' },
  { label: 'Todo', value: 'all' },
];

const DEFAULT_COLUMNS: Record<string, Array<{ field: string; label: string }>> = {
  orders: [
    { field: 'order_number', label: '#' },
    { field: 'customer_name', label: 'Cliente' },
    { field: 'total', label: 'Total' },
    { field: 'status', label: 'Estado' },
    { field: 'payment_status', label: 'Pago' },
    { field: 'created_at', label: 'Fecha' },
  ],
  crm_deals: [
    { field: 'title', label: 'Deal' },
    { field: 'amount', label: 'Monto' },
    { field: 'status', label: 'Estado' },
    { field: 'probability', label: 'Prob.' },
    { field: 'created_at', label: 'Fecha' },
  ],
  crm_leads: [
    { field: 'first_name', label: 'Nombre' },
    { field: 'last_name', label: 'Apellido' },
    { field: 'email', label: 'Email' },
    { field: 'status', label: 'Estado' },
    { field: 'source', label: 'Fuente' },
    { field: 'created_at', label: 'Fecha' },
  ],
  products: [
    { field: 'name', label: 'Nombre' },
    { field: 'price', label: 'Precio' },
    { field: 'stock_quantity', label: 'Stock' },
    { field: 'status', label: 'Estado' },
    { field: 'created_at', label: 'Fecha' },
  ],
  customers: [
    { field: 'name', label: 'Nombre' },
    { field: 'email', label: 'Email' },
    { field: 'total_orders', label: 'Órdenes' },
    { field: 'total_spent', label: 'Gastado' },
    { field: 'created_at', label: 'Fecha' },
  ],
  email_messages: [
    { field: 'to_email', label: 'Para' },
    { field: 'subject', label: 'Asunto' },
    { field: 'status', label: 'Estado' },
    { field: 'opened_count', label: 'Aperturas' },
    { field: 'created_at', label: 'Fecha' },
  ],
  payments: [
    { field: 'provider_code', label: 'Proveedor' },
    { field: 'amount', label: 'Monto' },
    { field: 'status', label: 'Estado' },
    { field: 'created_at', label: 'Fecha' },
  ],
};

export default function ReportSavedPage() {
  const storeNavigate = useStoreNavigate();
  const { tenantId } = useAuth();
  const toast = useAdminToast();


  // Create/Edit form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SavedReport | null>(null);
  const [rName, setRName] = useState('');
  const [rDesc, setRDesc] = useState('');
  const [rSource, setRSource] = useState('orders');
  const [rDateRange, setRDateRange] = useState('30d');
  const [rShared, setRShared] = useState(false);
  const [saving, setSaving] = useState(false);

  // Run report
  const [runningReport, setRunningReport] = useState<SavedReport | null>(null);
  const [reportResult, setReportResult] = useState<ReportResult | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportPage, setReportPage] = useState(1);

  const [deleteTarget, setDeleteTarget] = useState<SavedReport | null>(null);

  // Por la caché (ADR-0012, paso 3).
  const consulta = useQuery({
    queryKey: ['saved-reports', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<SavedReport[]> => {
      const { data, error } = await supabase
        .from('saved_reports')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as SavedReport[];
    },
  });

  const reports = useMemo(() => consulta.data ?? [], [consulta.data]);
  // 🪤 `isPending`, no `isFetching`: con la lista en pantalla, revalidar no es "cargando".
  const loading = !!tenantId && consulta.isPending;

  // 🪤 v5 sacó `onError`: avisar en el render sería un efecto en medio del render.
  useEffect(() => {
    if (consulta.error) toast.error('Error al cargar reportes', getErrorMessage(consulta.error));
  }, [consulta.error, toast]);

  const loadReports = useCallback(async () => { await consulta.refetch(); }, [consulta]);



  // ─── CRUD ─────────────────────────────────────────────────
  const resetForm = () => {
    setRName('');
    setRDesc('');
    setRSource('orders');
    setRDateRange('30d');
    setRShared(false);
  };

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (r: SavedReport) => {
    setEditing(r);
    setRName(r.name);
    setRDesc(r.description ?? '');
    setRSource(r.data_source);
    setRDateRange(r.date_range);
    setRShared(r.is_shared);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tenantId || !rName.trim()) return;
    setSaving(true);

    const columns = DEFAULT_COLUMNS[rSource] ?? [{ field: 'id', label: 'ID' }, { field: 'created_at', label: 'Fecha' }];

    const row = {
      tenant_id: tenantId,
      name: rName.trim(),
      description: rDesc.trim() || null,
      data_source: rSource,
      columns,
      date_range: rDateRange,
      is_shared: rShared,
      sort_dir: 'desc',
    };

    try {
      if (editing) {
        const { error } = await supabase.from('saved_reports').update(row).eq('id', editing.id);
        if (error) throw error;
        toast.success('Reporte actualizado');
      } else {
        const { error } = await supabase.from('saved_reports').insert(row);
        if (error) throw error;
        toast.success('Reporte creado');
      }
      setDialogOpen(false);
      await loadReports();
    } catch (error: unknown) {
      toast.error('Error al guardar', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { error } = await supabase.from('saved_reports').delete().eq('id', deleteTarget.id);
      if (error) throw error;
      toast.success('Reporte eliminado');
      setDeleteTarget(null);
      await loadReports();
    } catch (error: unknown) {
      toast.error('Error al eliminar', getErrorMessage(error));
    }
  };

  // ─── Run report ───────────────────────────────────────────
  const runReport = async (report: SavedReport, page = 1) => {
    setRunningReport(report);
    setReportLoading(true);
    setReportPage(page);

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) { toast.error('Sesión expirada'); return; }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/report-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          data_source: report.data_source,
          columns: report.columns.map(c => c.field),
          date_range: report.date_range,
          filters: report.filters,
          sort_by: report.sort_by || 'created_at',
          sort_dir: report.sort_dir,
          page,
          page_size: 25,
        }),
      });

      const data = await res.json();
      setReportResult(data as ReportResult);

      // Increment run count
      await supabase
        .from('saved_reports')
        .update({ run_count: report.run_count + 1, last_run_at: new Date().toISOString() })
        .eq('id', report.id);
    } catch {
      toast.error('Error al ejecutar reporte');
    } finally {
      setReportLoading(false);
    }
  };

  // ─── Export CSV ───────────────────────────────────────────
  const exportCsv = () => {
    if (!reportResult?.data || !runningReport) return;
    const columns = runningReport.columns;
    const header = columns.map(c => c.label).join(',');
    const rows = reportResult.data.map(row =>
      columns.map(c => {
        const val = row[c.field];
        if (val === null || val === undefined) return '';
        const str = String(val);
        return str.includes(',') ? `"${str}"` : str;
      }).join(','),
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${runningReport.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado');
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCellValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') return value.toLocaleString('es-PE', { maximumFractionDigits: 2 });
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      return new Date(value).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
    return String(value);
  };

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={ChartLineIcon}
          title="Reportes guardados"
          onBack={() => storeNavigate(ROUTES.APPS_REPORTS)}
          backAccessibilityLabel="Volver a Reportes"
        />
      }
      primaryAction={{ content: 'Crear reporte', onAction: openCreate }}
      secondaryActions={[
        { content: 'Dashboards', onAction: () => storeNavigate(ROUTES.APPS_REPORTS_DASHBOARDS) },
      ]}
    >
      <BlockStack gap="400">
        {/* Lista de reportes */}
        {loading ? (
          <Card><Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box></Card>
        ) : reports.length === 0 ? (
          <Card>
            <EmptyState heading="Crea tu primer reporte" image="" action={{ content: 'Crear reporte', onAction: openCreate }}>
              <Text as="p">Los reportes te permiten consultar datos de órdenes, CRM, productos y más con filtros personalizados y exportar a CSV.</Text>
            </EmptyState>
          </Card>
        ) : (
          <Card padding="0">
            <BlockStack gap="0">
              {reports.map((r, idx) => (
                <div key={r.id}>
                  <Box padding="400">
                    <InlineStack align="space-between" blockAlign="center">
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" fontWeight="medium">{r.name}</Text>
                          <Badge>{DATA_SOURCE_OPTIONS.find(d => d.value === r.data_source)?.label ?? r.data_source}</Badge>
                          <Badge tone="info">{DATE_RANGE_OPTIONS.find(d => d.value === r.date_range)?.label ?? r.date_range}</Badge>
                          {r.is_shared && <Badge tone="success">Compartido</Badge>}
                        </InlineStack>
                        {r.description && <Text as="p" tone="subdued" variant="bodySm">{r.description}</Text>}
                        <Text as="p" tone="subdued" variant="bodySm">
                          Ejecutado {r.run_count} veces · Último: {formatDate(r.last_run_at)}
                        </Text>
                      </BlockStack>
                      <InlineStack gap="100">
                        <Button icon={PlayIcon} onClick={() => runReport(r)} variant="primary" size="slim">Ejecutar</Button>
                        <Button icon={EditIcon} onClick={() => openEdit(r)} variant="plain" size="slim" />
                        <Button icon={DeleteIcon} onClick={() => setDeleteTarget(r)} variant="plain" tone="critical" size="slim" />
                      </InlineStack>
                    </InlineStack>
                  </Box>
                  {idx < reports.length - 1 && <Divider />}
                </div>
              ))}
            </BlockStack>
          </Card>
        )}

        {/* Resultados del reporte */}
        {runningReport && (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">Resultados: {runningReport.name}</Text>
                <InlineStack gap="200">
                  {reportResult?.data && reportResult.data.length > 0 && (
                    <Button icon={ExportIcon} onClick={exportCsv} size="slim">Exportar CSV</Button>
                  )}
                  <Button onClick={() => { setRunningReport(null); setReportResult(null); }} size="slim" variant="plain">Cerrar</Button>
                </InlineStack>
              </InlineStack>

              {reportLoading ? (
                <Box padding="400"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box>
              ) : reportResult?.data ? (
                <>
                  <Text as="p" tone="subdued" variant="bodySm">
                    {reportResult.total} resultado{reportResult.total !== 1 ? 's' : ''} · Página {reportResult.page}
                  </Text>
                  <IndexTable
                    headings={runningReport.columns.map(c => ({ title: c.label })) as [{ title: string }, ...{ title: string }[]]}
                    itemCount={reportResult.data.length}
                    selectable={false}
                  >
                    {reportResult.data.map((row, idx) => (
                      <IndexTable.Row id={String(row.id ?? idx)} key={idx} position={idx}>
                        {runningReport.columns.map((col) => (
                          <IndexTable.Cell key={col.field}>
                            {formatCellValue(row[col.field])}
                          </IndexTable.Cell>
                        ))}
                      </IndexTable.Row>
                    ))}
                  </IndexTable>
                  {reportResult.total > 25 && (
                    <InlineStack align="center">
                      <Pagination
                        hasPrevious={reportPage > 1}
                        onPrevious={() => runReport(runningReport, reportPage - 1)}
                        hasNext={reportPage * 25 < reportResult.total}
                        onNext={() => runReport(runningReport, reportPage + 1)}
                      />
                    </InlineStack>
                  )}
                </>
              ) : (
                <Text as="p" tone="subdued">Sin resultados.</Text>
              )}
            </BlockStack>
          </Card>
        )}
      </BlockStack>

      {/* Modal Crear/Editar */}
      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? 'Editar reporte' : 'Nuevo reporte'}
        primaryAction={{ content: 'Guardar', onAction: handleSave, loading: saving, disabled: !rName.trim() }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDialogOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField label="Nombre" value={rName} onChange={setRName} placeholder="Ej: Ventas mensuales" autoComplete="off" requiredIndicator />
            <TextField label="Descripción" value={rDesc} onChange={setRDesc} placeholder="Reporte de ventas agrupado por mes" autoComplete="off" multiline={2} />
            <Select label="Fuente de datos" options={DATA_SOURCE_OPTIONS} value={rSource} onChange={setRSource} />
            <Select label="Rango de fechas" options={DATE_RANGE_OPTIONS} value={rDateRange} onChange={setRDateRange} />
            <Checkbox label="Compartir con el equipo" checked={rShared} onChange={setRShared} />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Modal eliminar */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar reporte"
        primaryAction={{ content: 'Eliminar', onAction: handleDelete, destructive: true }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDeleteTarget(null) }]}
      >
        <Modal.Section>
          <Text as="p">¿Eliminar el reporte <strong>{deleteTarget?.name}</strong>?</Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
